/**
 * "Don't Teach (in Stem or Options)" (BCE's internal code "Z") is the one
 * SOCRATES criterion that's a judgment call rather than a defect, so it gets
 * an orange "?" presentation instead of the red X plus guidance asking the
 * teacher to decide — worded differently depending on whether the teaching
 * landed in the stem or in the options (Toni May / psychometrician request).
 *
 * Shared by the item card and the Edit-item modal so the two surfaces can't
 * drift on which half fired or on the copy itself.
 */

import { remapFeedbackLetters } from "@/utils/sortAnswers";

/** SOCRATES' flag key for the criterion (its label covers stem AND options). */
export const DONT_TEACH_KEY = "do_not_teach_in_stem";

// The criterion KEEPS its document name — "Don't Teach (in Stem or Options)"
// in the criteria grid, code and data (Toni, 2026-09-26: display-label change
// only). The Issues Found heading, however, names whichever half actually
// carries the teaching, so the teacher knows where to look:
export const DONT_TEACH_STEM_HEADING = "Don't Teach in Stem";
export const DONT_TEACH_OPTIONS_HEADING = "Don't Teach in Options";
export const DONT_TEACH_BOTH_HEADING = "Don't Teach in Stem and Options";

export const DONT_TEACH_STEM_GUIDANCE =
  "Stem contains information that may not be necessary to answer the question. " +
  "If removing the text would make the question unanswerable or ambiguous, keep it. " +
  "If not, edit or regenerate.";

export const DONT_TEACH_OPTIONS_GUIDANCE =
  "Option(s) contain information that may not be necessary to answer the question. " +
  "Review and determine if it should be kept, edited, or regenerated.";

// BCE emits this criterion's detail as "Z: Stem ..." / "Z: Option(s) ...",
// either joined with " | " in one entry or as separate `issues` entries. A
// bare "Z:" tag is too short for the snake_case criterion-prefix matching the
// issue-bucketing code does, so these messages have to be recognised directly.
// The "Z:" tag may also be absent entirely (older emissions, or the spread
// form the live payload uses): untagged "Stem ..." / "Option(s) ..." messages
// must still land on the tailored guidance instead of the generic orphan
// list. Untagged messages are only folded into this criterion when they read
// like the teaching-content finding, so a bare "Options A and D are
// identical" from another criterion can't be swallowed while Don't Teach
// happens to be flagged in the same cycle.
//
// An entirely different column tag may also precede the message: BCE's
// teaching-in-options finding (its DO_NOT_TEACH_IN_OPTIONS check, excluded
// from the 16-criterion framework) is emitted as "AC: Option(s) ..." and then
// rolled up into this criterion. The leading tag is stripped so the
// half-detection still sees the "Option(s)"/"Stem" marker and the teaching
// vocabulary behind it — otherwise the AC: wording (and its "AC:" tag) would
// leak through the generic renderers (ARC-221).
const DONT_TEACH_STEM_RE = /^\s*(?:[A-Z]{1,3}:\s*)?Stem\b/i;
const DONT_TEACH_OPTIONS_RE = /^\s*(?:[A-Z]{1,3}:\s*)?(?:Option\(\s*s\s*\)|Options\b)/i;

// A message belongs to this criterion when it reads like the teaching-content
// finding: options (or the stem) explain, define, or describe content instead
// of merely posing answer options. BCE phrases the same finding many ways —
// "carry tacked-on definitions", "contain teaching/definitions", "explain what
// inertia is instead of testing it" — all share this teaching vocabulary.
const TEACH_CONTENT_RE =
  /teach|\btacked-?on\b|\b(?:definition|definitions)\b|\b(?:describe|describes|described|describing|description|descriptions)\b|\b(?:define|defines|defined|defining)\b|\b(?:explain|explains|explained|explaining|explanation|explanatory)\b|\bterms?\s+only\b|\bmove the teaching out\b|\bput definitions in stem\b|\binstead of\s+(?:testing|asking)\b|\brather than\s+(?:testing|asking)\b/i;

const splitSegments = (raw: string): string[] =>
  raw.split(/\s*\|\s*/).map((s) => s.trim()).filter((s) => s.length > 0);

const scopedToHalf = (seg: string, halfRe: RegExp): boolean => {
  if (!halfRe.test(seg)) return false;
  if (/^\s*Z:/i.test(seg)) return true;
  return TEACH_CONTENT_RE.test(seg);
};

const isStemMessage = (raw: string): boolean =>
  splitSegments(raw).some((s) => scopedToHalf(s, DONT_TEACH_STEM_RE));

const isOptionsMessage = (raw: string): boolean =>
  splitSegments(raw).some((s) => scopedToHalf(s, DONT_TEACH_OPTIONS_RE));

/**
 * True for a raw issue message that belongs to this criterion. Callers use it
 * to keep the message out of their generic issue list — the tailored guidance
 * below already covers it, and leaving it in renders the same finding twice.
 */
export function isDontTeachIssue(raw: string): boolean {
  return isStemMessage(raw) || isOptionsMessage(raw);
}

export interface DontTeachGroup {
  /** Affected option letters in displayed (post-sort) order. Empty when the
   *  raw messages named no letters — the generic options copy is shown. */
  optionLetters: string[];
  /** The advisory copy rendered for this group. For the canonical
   *  teaching-content family this is the templated lettered advisory; for a
   *  distinct finding it is the specific, tidied finding text so nothing the
   *  validator said is lost. */
  message: string;
}

export interface DontTeachGuidance {
  flagged: boolean;
  /** The Issues Found / Open issues heading for this verdict, naming the
   *  half (or both halves) the teaching landed in — never the generic
   *  "in Stem or Options" label on this surface (Toni, 2026-09-26). */
  heading: string;
  showStem: boolean;
  showOptions: boolean;
  /** One entry per distinct affected-option letter set found in the raw
   *  options-scoped messages, in first-seen order. Messages that repeat the
   *  same finding — same criterion, same affected options, same semantic
   *  finding — collapse into one group so the teacher sees one advisory
   *  paragraph instead of N copies. Distinct findings stay separate even when
   *  they affect the same options, so genuinely different problems aren't
   *  silently merged. */
  optionGroups: DontTeachGroup[];
}

/** The options marker plus anything before it within the segment (tag,
 *  whitespace), consumed up to the letter list that BCE puts right after it. */
const OPTIONS_LETTERS_AFTER_RE = /^\s*(?:[A-Za-z]+\s*:\s*)?(?:Option\(\s*s\s*\)|Options)\s*/i;

/** The canonical teaching-content finding family: items where the options
 *  themselves teach (tacked-on definitions, "terms only", explaining content
 *  instead of testing, etc.). Different remediation passes phrase the same
 *  underlying finding differently ("carry tacked-on definitions..." /
 *  "contain teaching/definitions..." / "explain what X is instead of testing
 *  it"), so they all map to one semantic key and collapse. Any finding that
 *  isn't this family keeps its own key (e.g. "reveals the correct answer
 *  through grammatical cues") and never merges with it or with each other. */
const TEACHING_FINDING_RE =
  /teach|\btacked-?on\b|\b(?:definition|definitions)\b|\b(?:describe|describes|described|describing|description|descriptions)\b|\b(?:define|defines|defined|defining)\b|\b(?:explain|explains|explained|explaining|explanation|explanatory)\b|\bterms?\s+only\b|\bmove the teaching out\b|\bput definitions in stem\b|\binstead of\s+(?:testing|asking)\b|\brather than\s+(?:testing|asking)\b/i;

interface ParsedOptionsFinding {
  /** Affected option letters, remapped to display order and sorted. */
  letters: string[];
  /** Semantic discriminator. "teaching" for the canonical teaching-content
   *  family; otherwise a normalized copy of the finding text so distinct
   *  non-teaching findings never collide just for sharing letters. */
  kind: string;
  /** The advice/prose after the option letters (tag + marker stripped). */
  body: string;
}

/**
 * Split a raw issues entry into its per-segment options findings. A joined
 * "Z: Stem ... | Z: Option(s) ..." entry contributes its options segment here
 * (the stem segment is handled separately by the stem guidance).
 */
function parseOptionsFindings(raw: string, letterMap: Record<string, string> | undefined): ParsedOptionsFinding[] {
  const out: ParsedOptionsFinding[] = [];
  for (const seg of splitSegments(raw)) {
    if (!isOptionsMessage(seg)) continue;
    const marker = OPTIONS_LETTERS_AFTER_RE.exec(seg);
    if (!marker) continue;
    let rest = seg.slice(marker[0].length);
    const rawLetters: string[] = [];
    while (rest.length > 0) {
      const and = rest.match(/^and\b/i);
      if (and) {
        rest = rest.slice(and[0].length);
        continue;
      }
      const sep = rest.match(/^[\s,]+/);
      if (sep) {
        rest = rest.slice(sep[0].length);
        continue;
      }
      // An option letter is a STANDALONE single-capital token. "B" in
      // "B, D ..." is one; the "E" of a capitalized prose word like
      // "Options Explain ..." must not be — a word boundary after the
      // capital only exists when the capital isn't followed by lowercase
      // (i.e. it isn't part of a word). Without this, no-letter emissions
      // starting with a capital would fabricate an affected option.
      const letter = rest.match(/^[A-Z]\b/);
      if (!letter) break; // Prose begins ("carry tacked-on..." etc.)
      rawLetters.push(letter[0]);
      rest = rest.slice(1);
    }
    const body = rest.trim();
    out.push({
      letters: [...new Set(rawLetters)].map((l) => remapLetter(l, letterMap)).sort(),
      kind: TEACHING_FINDING_RE.test(body)
        ? "teaching"
        : `text:${body.toLowerCase().replace(/\s+/g, " ")}`,
      body,
    });
  }
  return out;
}

/** Rewrite a pre-sort letter through the question's answerLetterMap. */
const remapLetter = (
  letter: string,
  letterMap: Record<string, string> | undefined,
): string => letterMap?.[letter] ?? letter;

const teachingAdvisory = (letters: string[]): string =>
  letters.length > 0
    ? `Option(s) ${letters.join(", ")} contain information that may not be necessary to answer the question. Review and determine if it should be kept, edited, or regenerated.`
    : DONT_TEACH_OPTIONS_GUIDANCE;

function toGroup(f: ParsedOptionsFinding, letterMap: Record<string, string> | undefined): DontTeachGroup {
  if (f.kind === "teaching") {
    return { optionLetters: f.letters, message: teachingAdvisory(f.letters) };
  }
  // A distinct finding keeps its own specifics so it isn't silently folded
  // into an advisory that only names options. Rebuild the copy from the
  // remapped letters and the tidied advice — nothing the validator wrote is
  // dropped.
  const body = remapFeedbackLetters(f.body, letterMap);
  const message = `${f.letters.length > 0 ? `Option(s) ${f.letters.join(", ")} ` : ""}${body}`.trim();
  return { optionLetters: f.letters, message };
}

/**
 * Groups options-scoped messages by (affected-option letter set, semantic
 * finding). Letters are remapped through the question's pre-sort→post-sort
 * answerLetterMap (BCE scores the pre-sort option order; the UI displays the
 * sorted one). Messages naming the same options for the same semantic finding
 * collapse into one group; messages that are genuinely different findings keep
 * their own groups even with identical letters. A bare "Option(s)..." message
 * (no letters) claims the same thing as a lettered one of the same finding
 * without narrowing it, so when exactly one lettered group of that finding
 * exists it absorbs the bare message — one paragraph, letters preserved. With
 * several lettered groups a bare message can't be attributed to one of them,
 * so it stands alone so no finding is dropped.
 */
function groupOptionMessages(
  messages: string[],
  letterMap: Record<string, string> | undefined,
): DontTeachGroup[] {
  const groups = new Map<string, ParsedOptionsFinding>();
  for (const raw of messages) {
    for (const parsed of parseOptionsFindings(raw, letterMap)) {
      const key = `${parsed.kind}\u0000${parsed.letters.join("")}`;
      if (!groups.has(key)) groups.set(key, parsed);
    }
  }

  const byKind = new Map<string, ParsedOptionsFinding[]>();
  for (const f of groups.values()) {
    const list = byKind.get(f.kind) ?? [];
    list.push(f);
    byKind.set(f.kind, list);
  }

  const result: DontTeachGroup[] = [];
  for (const list of byKind.values()) {
    const lettered = list.filter((f) => f.letters.length > 0);
    const bare = list.filter((f) => f.letters.length === 0);
    for (const f of lettered) result.push(toGroup(f, letterMap));
    if (bare.length > 0 && lettered.length !== 1) {
      // No single lettered group to attribute the bare message to (either zero
      // or several distinct lettered findings) — keep it so no finding drops.
      for (const f of bare) result.push(toGroup(f, letterMap));
    }
    // bare.length > 0 && lettered.length === 1 → absorbed above.
  }
  return result;
}

/** The options advisory copy for a group (see DontTeachGroup.message). */
export function dontTeachOptionsMessage(group: DontTeachGroup): string {
  return group.message;
}

/**
 * Which guidance paragraphs to render for a scored cycle. When the messages
 * name both halves — or name neither, including when BCE sends no detail at
 * all — both halves are shown rather than guessing wrong and pointing the
 * teacher at the wrong part of the item.
 *
 * `letterMap` is the question's pre-sort→post-sort answerLetterMap; pass it so
 * the affected-option letters shown match the sorted options on screen. No-op
 * when undefined (question was never re-ordered, or the caller already scored
 * against displayed order).
 *
 * `qualityFeedback` is the advisory array for the displayed cycle (see
 * feedbackForCycle in utils/socratesScore). This criterion's own entries —
 * when the finding is carried there rather than in the raw issues list — also
 * count toward which half fired, so the stem/options heading and body stay
 * correct no matter which channel the analyzer used.
 */
export function dontTeachGuidance(
  flags: Record<string, number>,
  issues: string[],
  letterMap?: Record<string, string>,
  qualityFeedback?: ReadonlyArray<{ criterion: string; description: string }>,
): DontTeachGuidance {
  const flagged = flags[DONT_TEACH_KEY] === 1;
  const feedbackMessages = (qualityFeedback ?? [])
    .filter((f) => (f.criterion ?? "") === DONT_TEACH_KEY)
    .map((f) => (f.description ?? "").trim())
    .filter((s) => s.length > 0);
  // Fold the feedback descriptions into the message pool so the stem/options
  // split still works when the finding lives only in quality_feedback.
  const messages = [...issues, ...feedbackMessages];
  const stemFired = flagged && messages.some(isStemMessage);
  const optionsFired = flagged && messages.some(isOptionsMessage);
  const bothFired = stemFired && optionsFired;
  // Flagged with no scoped message at all: don't guess which half — both
  // halves keep showing, exactly the pre-existing no-detail behaviour.
  const neitherFired = flagged && !stemFired && !optionsFired;
  return {
    flagged,
    heading: bothFired || neitherFired
      ? DONT_TEACH_BOTH_HEADING
      : stemFired
        ? DONT_TEACH_STEM_HEADING
        : DONT_TEACH_OPTIONS_HEADING,
    showStem: flagged && (stemFired || neitherFired),
    showOptions: flagged && (optionsFired || neitherFired),
    optionGroups: optionsFired ? groupOptionMessages(messages, letterMap) : [],
  };
}

/**
 * The per-item rewrite BCE attaches to a Don't Teach verdict: the analyzer
 * emits severity=advisory plus a suggestion (e.g. a rewritten stem) in
 * quality_feedback, scoped to the Don't Teach criterion. Returns the first
 * non-empty advisory suggestion for THIS criterion, or null when none is
 * present so the surfaces render the static guidance alone. The `criterion`
 * filter is essential — other criteria can carry their own advisory entries
 * in the same array, and surfacing one of those under Don't Teach would be
 * the same false attribution ARC-66 exists to eliminate.
 *
 * The live API delivers `suggestion: null` on most rows, so a null or blank
 * suggestion in an early entry is skipped and scanning continues for a later
 * non-empty one instead of aborting the search (ARC-302).
 */
export function dontTeachAdvisorySuggestion(
  qualityFeedback?: Array<{ severity: string; criterion: string; suggestion?: string | null }>,
): string | null {
  if (!qualityFeedback || qualityFeedback.length === 0) return null;
  for (const f of qualityFeedback) {
    if (
      (f.criterion ?? "") === DONT_TEACH_KEY &&
      f.severity === "advisory"
    ) {
      const suggestion = f.suggestion?.trim();
      if (suggestion) return suggestion;
    }
  }
  return null;
}
